import { ArrowLeftOutlined, DeleteOutlined, PlayCircleOutlined } from '@ant-design/icons'
import { Link, useNavigate } from '@tanstack/react-router'
import { Button, Card, Descriptions, Flex, Popconfirm, Space, Table, Tag, Tooltip, Typography } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useAuth } from '../../auth'
import { NetworkErrors } from '../../components/network-errors'
import { TimeAgo } from '../../components/time-ago'
import { usePagination } from '../../hooks/use-pagination'
import { DESTROY_WEBHOOK } from '../../user/permission'
import { webhookEventTypeLabel } from '../event-type'
import { useDeleteWebhook, useSendTestWebhook, useWebhook, useWebhookDeliveries } from '../hooks'
import { WebhookDelivery } from '../webhook'
import { searchQuery } from '../../zod/pagination'

export function WebhookShow({
    webhookId,
    search,
}: {
    webhookId: string
    search: { query?: z.infer<typeof searchQuery> }
}) {
    const { t } = useTranslation()
    const { can } = useAuth()
    const navigate = useNavigate()
    const webhookQuery = useWebhook(webhookId)
    const tableProps = usePagination(useWebhookDeliveries, {
        webhookId,
        query: search.query,
    })
    const sendTestWebhook = useSendTestWebhook()
    const deleteWebhook = useDeleteWebhook()

    if (webhookQuery.isPending || webhookQuery.isError) {
        return (
            <NetworkErrors
                query={webhookQuery}
                result404={{
                    title: t('Webhook not found'),
                }}
            />
        )
    }

    const webhook = webhookQuery.data

    return (
        <Flex
            vertical
            gap="middle"
        >
            <Space>
                <Link
                    to="/integrations"
                    search={{ view: 'webhooks' }}
                >
                    <Button icon={<ArrowLeftOutlined />}>{t('Back to webhooks')}</Button>
                </Link>
            </Space>

            <Card>
                <Flex
                    justify="space-between"
                    align="start"
                    gap="middle"
                    className="flex-col md:flex-row"
                >
                    <Flex
                        vertical
                        gap="small"
                    >
                        <Typography.Title
                            level={4}
                            className="mb-0"
                        >
                            {webhookEventTypeLabel(webhook.eventType)}
                        </Typography.Title>
                        <Typography.Text>{webhook.url}</Typography.Text>
                        <Space>
                            <Tag color={webhook.active ? 'green' : 'red'}>
                                {webhook.active ? t('Active') : t('Inactive')}
                            </Tag>
                            <Typography.Text type="secondary">
                                {t('Created')} <TimeAgo date={webhook.createdAt} />
                            </Typography.Text>
                        </Space>
                    </Flex>

                    <Space>
                        <Button
                            icon={<PlayCircleOutlined />}
                            disabled={!webhook.active}
                            loading={sendTestWebhook.isPending}
                            onClick={() => {
                                sendTestWebhook.mutate(webhook.id)
                            }}
                        >
                            {t('Test')}
                        </Button>

                        <Popconfirm
                            title={t('Are you sure you want to delete this webhook?')}
                            description={t('Events will stop being sent to this webhook immediately.')}
                            okText={t('Yes, delete')}
                            cancelText={t('Cancel')}
                            okButtonProps={{ danger: true }}
                            onConfirm={async () => {
                                await deleteWebhook.mutateAsync(webhook.id)
                                navigate({
                                    to: '/integrations',
                                    search: { view: 'webhooks' },
                                })
                            }}
                        >
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                                disabled={!can(DESTROY_WEBHOOK)}
                                loading={deleteWebhook.isPending}
                            >
                                {t('Delete')}
                            </Button>
                        </Popconfirm>
                    </Space>
                </Flex>
            </Card>

            <Card title={t('Deliveries')}>
                <div className="max-w-full overflow-x-auto">
                    <Table
                        {...tableProps}
                        rowKey="id"
                        size="small"
                        pagination={{
                            defaultPageSize: 10,
                        }}
                        scroll={{ x: 900 }}
                        locale={{ emptyText: t('No deliveries') }}
                        expandable={{
                            expandRowByClick: true,
                            expandedRowRender: (delivery: WebhookDelivery) => (
                                <Flex
                                    vertical
                                    gap="middle"
                                    className="max-w-[900px]"
                                >
                                    <Descriptions
                                        size="small"
                                        bordered
                                        column={1}
                                    >
                                        <Descriptions.Item label={t('Event')}>
                                            {webhookEventTypeLabel(delivery.eventType)}
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('Attempt')}>{delivery.attempt}</Descriptions.Item>
                                        <Descriptions.Item label={t('Type')}>
                                            {delivery.test ? t('Test') : t('Live')}
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('HTTP Status')}>
                                            {delivery.httpStatus ?? t('Unknown')}
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('Delivered')}>
                                            {delivery.deliveredAt ? (
                                                <TimeAgo date={delivery.deliveredAt} />
                                            ) : (
                                                t('Not delivered')
                                            )}
                                        </Descriptions.Item>
                                        {delivery.errorMessage && (
                                            <Descriptions.Item label={t('Error')}>
                                                {delivery.errorMessage}
                                            </Descriptions.Item>
                                        )}
                                    </Descriptions>

                                    <Flex gap="middle">
                                        <Card
                                            size="small"
                                            title={t('Request')}
                                            className="w-full min-w-0 max-w-full overflow-hidden xl:w-1/2"
                                        >
                                            <JsonBlock value={delivery.requestPayload} />
                                        </Card>

                                        <Card
                                            size="small"
                                            title={t('Response')}
                                            className="w-full min-w-0 max-w-full overflow-hidden xl:w-1/2"
                                        >
                                            <JsonBlock
                                                value={
                                                    formatResponseBody(delivery.responseBody) ?? t('No response body')
                                                }
                                            />
                                        </Card>
                                    </Flex>
                                </Flex>
                            ),
                        }}
                        columns={tableProps.withColumnDefaults([
                            {
                                title: t('Status'),
                                key: 'status',
                                width: 140,
                                render: (delivery: WebhookDelivery) => {
                                    const tag = (
                                        <Tag color={delivery.status === 'success' ? 'green' : 'red'}>
                                            {delivery.status === 'success' ? t('Success') : t('Failed')}
                                        </Tag>
                                    )

                                    if (delivery.status === 'failed' && delivery.errorMessage) {
                                        return <Tooltip title={delivery.errorMessage}>{tag}</Tooltip>
                                    }

                                    return tag
                                },
                            },
                            {
                                title: t('Type'),
                                key: 'test',
                                width: 120,
                                render: (delivery: WebhookDelivery) => (
                                    <Tag>{delivery.test ? t('Test') : t('Live')}</Tag>
                                ),
                            },
                            {
                                title: t('HTTP Status'),
                                dataIndex: 'httpStatus',
                                key: 'httpStatus',
                                width: 120,
                                render: (httpStatus: number | null) => httpStatus ?? t('Unknown'),
                            },
                            {
                                title: t('Delivered'),
                                key: 'deliveredAt',
                                width: 180,
                                render: (delivery: WebhookDelivery) =>
                                    delivery.deliveredAt ? <TimeAgo date={delivery.deliveredAt} /> : t('Never'),
                            },
                            {
                                title: t('Created'),
                                dataIndex: 'createdAt',
                                key: 'createdAt',
                                width: 180,
                                render: (createdAt: Date) => <TimeAgo date={createdAt} />,
                            },
                        ])}
                    />
                </div>
            </Card>
        </Flex>
    )
}

function JsonBlock({ value }: { value: string }) {
    return (
        <pre className="m-0 max-h-96 w-full max-w-full overflow-auto rounded-md bg-slate-50 p-3 text-xs leading-5 whitespace-pre-wrap break-words">
            {value}
        </pre>
    )
}

function formatResponseBody(responseBody: string | null) {
    if (!responseBody) {
        return null
    }

    try {
        return JSON.stringify(JSON.parse(responseBody), null, 2)
    } catch {
        return responseBody
    }
}
